import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { apiService } from '../../services/api';
import { UserPublicSummary } from '../../types';
import FollowButton from './FollowButton';

interface FollowersFollowingModalProps {
  userId: number;
  type: 'followers' | 'following';
  isOpen: boolean;
  onClose: () => void;
}

const FollowersFollowingModal: React.FC<FollowersFollowingModalProps> = ({
  userId,
  type,
  isOpen,
  onClose,
}) => {
  const [users, setUsers] = useState<UserPublicSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen, userId, type]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response =
        type === 'followers'
          ? await apiService.getUserFollowers(userId)
          : await apiService.getUserFollowing(userId);
      setUsers(response.users);
    } catch (error) {
      console.error(`Failed to load ${type}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async (targetUserId: number) => {
    const user = users.find((u) => u.user_id === targetUserId);
    if (!user) return;

    try {
      if (user.is_following) {
        await apiService.unfollowUser(targetUserId);
      } else {
        await apiService.followUser(targetUserId);
      }
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === targetUserId
            ? { ...u, is_following: !u.is_following }
            : u
        )
      );
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
            {type}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              Loading...
            </p>
          )}

          {!loading && users.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              No {type} yet.
            </p>
          )}

          {!loading && users.length > 0 && (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      @{user.username}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.public_project_count} public projects
                    </p>
                  </div>
                  <FollowButton
                    userId={user.user_id}
                    isFollowing={user.is_following}
                    onToggle={handleFollowToggle}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowersFollowingModal;
